import {
  isTrialCourseCtaEligible,
  TRIAL_CTA_TARGET,
} from "@/lib/trialCourseCta";
import type { UserSubscriptionStateRow } from "@/types/userSubscriptionState";
import type { HubCtaTarget } from "@/types/hubCtas";

/**
 * Map subscription → hub CTA targets (lifetime → none).
 *
 * `user_subscription_state` holds a row for everyone who ever had a plan 21/22
 * membership (backfilled + kept fresh by the WP bridge), so a missing row is a
 * genuine non-member. Callers must only pass a resolved query result — gate on
 * the query having settled so a still-loading null doesn't flash Join at
 * members.
 */
export function hubCtaTargetsForSubscription(
  row: UserSubscriptionStateRow | null,
): HubCtaTarget[] {
  if (row?.is_lifetime === true) return [];

  const tier = row?.tier ?? null;
  const targets: HubCtaTarget[] = [];

  if (!row || tier === "none") {
    targets.push("non_member");
  }
  if (tier === "regular") {
    targets.push("regular");
  }
  if (row && row.billing_interval === "month" && tier !== "none") {
    targets.push("monthly");
  }

  return targets;
}

/**
 * Targets for a user. Trial members in the "Jazz Piano First Steps" audience
 * get the trial target first; their subscription targets are still fetched so
 * that, while no trial CTA row is enabled in the CMS, they keep seeing the
 * regular banners (SubscriptionHubCtas hides those only once a trial row exists).
 */
export function hubCtaTargetsForUser(input: {
  subscription: UserSubscriptionStateRow | null;
  userCreatedAt: string | null | undefined;
  preferenceMetadata: Record<string, unknown> | null | undefined;
  appleTrial?: { onTrial: boolean } | null;
}): HubCtaTarget[] {
  const targets = hubCtaTargetsForSubscription(input.subscription);
  if (isTrialCourseCtaEligible(input)) return [TRIAL_CTA_TARGET, ...targets];
  return targets;
}
